import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useNotification } from 'hooks'
import { useManagerQuery } from '../../api'
import { InfoItems } from 'components/core-ui/InfoItems'
import { Box, CircularProgress } from '@mui/material'
import style from '../../partners.modules.scss'
import ActionComponents from '../../components/ActionComponents'
import { DeleteModal } from '../components/DeleteModal'
import AddDelegateToManagerModal from '../AddDelegateToManagerModal/AddDelegateToManagerModal'
// import { Table } from '@fullscreendigital/carrefour-dex-fe'
import EditManagerModal from '../EditManager/EditManagerModal'
import { formatUIDateTime } from 'utils/helpers'
import { DeleteDelegateModal } from '../AddDelegateToManagerModal/components/DeleteDelegateModal'
import { ListItemProps } from '../../interfaces'
import ViewActionIcon from 'icons/ViewActionIcon'
import {
  assignmentsColumns as defaultAssignmentsColumns,
  delegatesColumns as defaultDelegatesColumns
} from './helpers'
import { AddActionIcon, DeleteActionIcon } from 'icons'
import { Link } from 'components/core-ui/Link'
import { URLS } from 'components/iam/constants'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { formatErrorMessage } from 'utils/formatting'
import { Table } from 'components/core-ui/Table'

const ManagerDetails: React.FC = () => {
  const [isAddDelegateModalOpen, setIsAddDelegateModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteDelegateModalOpen, setIsDeleteDelegateModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [currentDelegateId, setCurrentDelegateId] = useState<number | null>(null)

  const [shouldRefreshAssignmentsData, setShouldRefreshAssignmentsData] = useState<boolean>(false)
  const [shouldResetAssignmentsPage, setShouldResetAssignmentsPage] = useState<boolean>(false)
  const [assignmentsTableItems, setAssignmentsTableItems] = useState<any[]>([])
  const [isAssignmentsLoading, setIsAssignmentsLoading] = useState<boolean>(true)
  const [totalactiveAssignmentsCount, setTotalactiveAssignmentsCount] = useState<number>(0)

  const [shouldRefreshDelegatesData, setShouldRefreshDelegatesData] = useState<boolean>(false)
  const [shouldResetDelegatesPage, setShouldResetDelegatesPage] = useState<boolean>(false)
  const [delegatesTableItems, setDelegatesTableItems] = useState<any[]>([])
  const [isDelegatesLoading, setIsDelegatesLoading] = useState<boolean>(true)
  const [totalDelegatesCount, setTotalDelegatesCount] = useState<number>(0)

  const { canEdit, canDelete, canDeleteDelegate } = {
    canEdit: true,
    canDelete: false,
    canDeleteDelegate: true
  }

  const { id } = useParams<{ id: string }>()
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const {
    data: manager,
    isLoading,
    refetch
  } = useManagerQuery(showNotification, Number(id), shouldRefreshData)

  const assignmentsColumns = useMemo(() => defaultAssignmentsColumns, [])
  const delegatesColumns = useMemo(() => defaultDelegatesColumns, [])

  const openEditModal = () => setIsEditModalOpen(true)
  const closeEditModal = () => setIsEditModalOpen(false)

  const openAddDelegateModal = () => setIsAddDelegateModalOpen(true)
  const closeAddDelegateModal = () => setIsAddDelegateModalOpen(false)
  const openDeleteDelegateModal = () => setIsDeleteDelegateModalOpen(true)
  const closeDeleteDelegateModal = () => {
    setIsDeleteDelegateModalOpen(false)
  }

  const openDeleteModal = () => setIsDeleteModalOpen(true)
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
    navigate(-1)
  }

  const delegateDeleteAction = (delegateId: number) => {
    setCurrentDelegateId(delegateId)
    openDeleteDelegateModal()
  }

  function handleAssignmentClick(e, item: ListItemProps): void {
    navigate(`/iam/externalPartners/assignments/${item?.index}`)
  }

  if (isLoading)
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  if (!manager) return <div>Manager not found</div>

  // const activeDelegateExists = manager.delegates?.length > 0

  const fetchAssignmentsData = async ({ page, rowsPerPage, order, orderBy, searchText }) => {
    setIsAssignmentsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/assignments',
          tableFilters: [{ name: 'manager.id', value: id }],
          searchText,
          searchFields: ['globalId', 'jobTitle', 'person.fullName'],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setAssignmentsTableItems(response.list || [])
      setTotalactiveAssignmentsCount(response.page.totalCount)
      setIsAssignmentsLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching assignments. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const fetchDelegatesData = async ({ page, rowsPerPage, order, orderBy }) => {
    setIsDelegatesLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/delegates',
          tableFilters: [{ name: 'managers.id', value: id }],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setDelegatesTableItems(response.list || [])
      setTotalDelegatesCount(response.page.totalCount)
      setIsDelegatesLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching delegates. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  return (
    <div className={style.mainContainer}>
      <div className={style.breadcrumbsContainer}>
        <Link href="/iam/partners" className={style.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <Link href={URLS.IAM_MANAGERS} className={style.breadcrumbsLink}>
          Managers
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <span className={style.breadcrumbsCurrent}>
          {manager.firstName + ' ' + manager.lastName}
        </span>
      </div>
      {canEdit && (
        <>
          <EditManagerModal
            isModalOpen={isEditModalOpen}
            onModalClose={closeEditModal}
            managerId={manager.id}
            onSuccess={refetch}
          />
          <AddDelegateToManagerModal
            isModalOpen={isAddDelegateModalOpen}
            onModalClose={closeAddDelegateModal}
            manager={manager}
            setShouldRefreshData={() => setShouldRefreshDelegatesData(true)}
          />
        </>
      )}
      {canDelete && (
        <DeleteModal
          isModalOpen={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          itemId={manager.id}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      {canDeleteDelegate && (
        <DeleteDelegateModal
          isModalOpen={isDeleteDelegateModalOpen}
          closeModal={closeDeleteDelegateModal}
          managerId={manager.id}
          itemId={currentDelegateId}
          setShouldRefreshData={() => setShouldRefreshDelegatesData(true)}
        />
      )}

      <Box className={style.flexContainer} sx={{ justifyContent: 'stretch' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              { name: manager.firstName, label: 'First Name:' },
              {
                name: `${
                  manager.assignmentList?.filter(
                    assignment => assignment.person.isEnabled && assignment.status.name === 'Active'
                  )?.length
                }`,
                label: 'Partners assigned:'
              },
              { name: manager.lastName, label: 'Last Name:' },
              {
                name: `${manager.delegates?.filter(delegate => delegate.isEnabled)?.length}`,
                label: 'Delegates:'
              },
              { name: manager.email, label: 'Email:' },
              { name: manager.phone, label: 'Phone number:' },
              { name: manager.isEnabled ? 'Enabled' : 'Disabled', label: 'Status:' }
            ]}
            title={'Manager details'}
            actionComponents={<ActionComponents openEditModal={openEditModal} />}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              { name: manager.createdBy, label: 'Created By:' },
              { name: formatUIDateTime(manager.createdDate), label: 'Created Date:' },
              { name: manager.lastModifiedBy, label: 'Modified By:' },
              { name: formatUIDateTime(manager.lastModifiedDate), label: 'Modified Date:' },
              { name: manager.id.toString(), label: 'Manager ID:' }
            ]}
            title={'Manager metadata'}
          />
        </Box>
      </Box>
      <Box className={style.infoTableContainer}>
        <Box sx={{ flex: 1, marginTop: -5 }}>
          <Box sx={{ flex: 1 }}>
            <Table
              hideCheckbox
              hasSearchInput
              orderBy="person.firstName"
              order="asc"
              title="Assignments"
              columns={assignmentsColumns}
              items={assignmentsTableItems}
              fetchData={fetchAssignmentsData}
              isLoading={isAssignmentsLoading}
              totalCount={totalactiveAssignmentsCount}
              shouldRefreshData={shouldRefreshAssignmentsData}
              setShouldRefreshData={setShouldRefreshAssignmentsData}
              shouldResetPage={shouldResetAssignmentsPage}
              setShouldResetPage={setShouldResetAssignmentsPage}
              rowActions={[
                {
                  label: 'Details',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  desktopIcon: <ViewActionIcon />,
                  onClick: (_e, rowId) => navigate(`/iam/externalPartners/assignments/${rowId}`),
                  backgroundColor: ''
                }
              ]}
              rowActionsWidth="80px"
              desktopRowActionsWidth="50px"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Table
              hideCheckbox
              orderBy="firstName"
              order="asc"
              title="Dedicated delegate"
              columns={delegatesColumns}
              items={delegatesTableItems}
              fetchData={fetchDelegatesData}
              isLoading={isDelegatesLoading}
              totalCount={totalDelegatesCount}
              shouldRefreshData={shouldRefreshDelegatesData}
              setShouldRefreshData={setShouldRefreshDelegatesData}
              shouldResetPage={shouldResetDelegatesPage}
              setShouldResetPage={setShouldResetDelegatesPage}
              rowActions={[
                {
                  label: 'Delete',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  desktopIcon: <DeleteActionIcon />,
                  onClick: (_e, rowId) => delegateDeleteAction(Number(rowId)),
                  backgroundColor: ''
                },
                {
                  label: 'Details',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  desktopIcon: <ViewActionIcon />,
                  onClick: (_e, rowId) => navigate(`/iam/externalPartners/delegates/${rowId}`),
                  backgroundColor: ''
                }
              ]}
              disablePagination
              rowActionsWidth="130px"
              desktopRowActionsWidth="100px"
              toolbarActions={
                !delegatesTableItems.length
                  ? [
                      {
                        label: 'Assign Delegate',
                        icon: <AddActionIcon />,
                        onClick: openAddDelegateModal
                      }
                    ]
                  : []
              }
            />
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default ManagerDetails
